import { useMemo, useState, useEffect, useContext } from 'react'
import { useTheme } from '@mui/material/styles'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { MenuItem, Box, IconButton, Tooltip, Button } from '@mui/material'

import _ from 'lodash'

import EditIcon from '@mui/icons-material/Edit'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import PersonRemoveAlt1OutlinedIcon from '@mui/icons-material/PersonRemoveAlt1Outlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { getUsers, addUser, deleteUser, saveUser } from '/providers/clients-api'

import DataExportDialog from '/components/data-export-dialog'
import excelExport from './user-list-to-excel'
import { confirm } from '/components/confim-dialog'
import EditUserDialog from './edit-user-dialog'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'

const UserAdmin = () => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])

  const { apiSource, apiKeys, setProcessing, user } = useContext(AppContext)

  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState()


  useEffect(() => {
    getUserList()
  }, [])

  const getUserList = async () => {
    setLoading(true)
    const u = await getUsers({ apiSource, apiKeys })
    setLoading(false)
    if (u.ok) setUsers(u.users)
    // TODO Add error handling
  }

  const addClientUser = () => {
    setEditingUser({})
    setEditUserDialogOpen(true)
  }

  const saveEditedUser = (mode, user) => {
    switch (mode) {
      case "add": return doAddUser(user)
      case "edit": return doSaveUser(user)
    }
  }

  const doAddUser = async user => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Adding new user', variant: 'light' })
    const k = await addUser({ apiSource, apiKeys, user })
    setProcessing(false)
    // TODO Add error handling
    if (k.ok) {
      enqueueSnackbar({ message: 'User added', variant: 'success' })
      setUsers(k.users)
    }
  }

  const editClientUser = user_id => {
    const usr = users.filter(u => u.user_id === user_id)[0]
    setEditingUser(usr)
    setEditUserDialogOpen(true)
  }

  const doSaveUser = async user => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Saving user', variant: 'light' })
    const k = await saveUser({ apiSource, apiKeys, user })
    setProcessing(false)
    if (k.ok) {
      enqueueSnackbar({ message: 'User saved', variant: 'success' })
      setUsers(k.users)
    }
    // TODO Add error handling
  }

  const deleteClientUser = async userId => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this user rather than temporarily disable them?'
    })

    if (confirmed) {
      setProcessing(true)
      enqueueSnackbar({ message: 'Removing user', variant: 'light' })
      const k = await deleteUser({ apiSource, apiKeys, userId })
      enqueueSnackbar({ message: 'User removed', variant: 'success' })
      setProcessing(false)

      setUsers(k)
    }
  }


  const toggleUserEnabled = async userId => {

    const user = users.filter(u => u.user_id === userId)[0]
    user.user_enabled = 1 - user.user_enabled

    setProcessing(true)
    enqueueSnackbar({ message: 'Setting key status', variant: 'light' })
    const k = await saveClientUser({ apiSource, apiKeys, user })
    setProcessing(false)

    if (k.ok) {
      enqueueSnackbar({ message: 'Key status saved', variant: 'success' })
      setUsers(k.users)
    }
    // TODO Add error handling
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'user_id',
      header: 'User ID',
      filterVariant: 'autocomplete',
      size: 80,
    },
    {
      accessorKey: 'username',
      header: 'username',
      filterVariant: 'autocomplete',
      size: 200,
      Cell: ({ cell, row }) => <div className='link' onClick={() => { editClientUser(row.id) }}><OpenInNewIcon className='mr-5' />{cell.getValue()}</div>
    },
    {
      accessorKey: 'email',
      header: 'Email Address',
      filterVariant: 'autocomplete',
      size: 200,
    },
    {
      accessorKey: 'user_enabled',
      header: 'Enabled',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'client_admin_enabled',
      header: 'Administrator',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'studio_enabled',
      header: 'Studio',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'scanner_enabled',
      header: 'Scanner',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
  ])

  const table = useMaterialReactTable({
    columns,
    data: users,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.user_id,
    ...tableStyles,
    renderRowActionMenuItems: ({ closeMenu, row }) => getActions(editClientUser, toggleUserEnabled, deleteClientUser, closeMenu, user, row),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row items-start gap-5'>
        <Button startIcon={<PersonAddOutlinedIcon className='mr-2' />} onClick={addClientUser}>Add User</Button>
      </div>
    )
  })

  return (
    <>
      <Box sx={{ height: 'calc(100vh - 185px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
          <MaterialReactTable table={table} />
        </StyledPaper>
      </Box>
      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName="users" data={users} excelExport={excelExport} />
      <EditUserDialog editingUser={editingUser} save={saveEditedUser} open={editUserDialogOpen} setOpen={setEditUserDialogOpen} ownUser={user} />
    </>
  )
}

export default UserAdmin


const getActions = (editClientUser, toggleUserEnabled, deleteClientUser, closeMenu, user, row) => {

  if (user.user_id !== row.id) return [
    <MenuItem key={1} onClick={() => { editClientUser(row.id); closeMenu() }}><EditIcon className='mr-2' /> Edit User</MenuItem>,
    <MenuItem key={3} onClick={() => { toggleUserEnabled(row.id); closeMenu() }}>{row.original.user_enabled === 1 ? <PersonOffOutlinedIcon className='mr-2' /> : <PersonOutlineOutlinedIcon className='mr-2' />}{row.original.user_enabled === 1 ? "Disable" : "Enabled"} User</MenuItem>,
    <MenuItem key={2} onClick={() => { deleteClientUser(row.id); closeMenu() }}><PersonRemoveAlt1OutlinedIcon className='mr-2' /> Delete User</MenuItem>,
  ]

  // Can't disable or delete your own account!
  else return [
    <MenuItem key={1} onClick={() => { editClientUser(row.id); closeMenu() }}><EditIcon className='mr-2' /> Edit User</MenuItem>,
  ]
}